import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowRightLeft, Check, ChevronRight, CircleAlert, FileSpreadsheet, GitBranch, Loader2, MoreHorizontal, Plus,
  RefreshCw, Table2, Trash2, Workflow,
} from "lucide-react";
import { toast } from "react-toastify";

import StagePage from "src/stage/StagePage";
import { Skeleton, formatNumber } from "src/stage/StageUi";
import { characterFor } from "src/stage/characters";
import { getPipelines, clearPipelineAndLeadsCache, clearAllStages } from "../CachedData";
import { deletePipeline } from "../Workspace/services/PipelineApi";
import { getErrorMessage } from "../Workspace/services/workspaceService";
import workflowService from "./services/workflowService";
import CreatePipelineDrawer from "../components/Leads/CreatePipelineDrawer";
import PipelineTemplateGallery from "./PipelineTemplateGallery";
import { HelpTip } from "src/features/Stage/shared/StageKit";
import EditPipelineSheetDrawer from "../components/PipelineImport/EditPipelineSheetDrawer";
import PipelineSharingSettings from "../../../common/customerSharing/PipelineSharingSettings";
import "src/features/Stage/home.css";
import "./pipelines-stage.css";

const SELECTED_PIPELINE_KEY = "selectedPipelineId";
const agent = characterFor("appy");

function PipelineMenu({ pipeline, current, onCurrent, onSharing, onImport, onDelete }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => { if (!rootRef.current?.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onPointer); document.removeEventListener("keydown", onKey); };
  }, [open]);
  const run = (action) => { setOpen(false); action(); };
  return (
    <div className="ws-dd-root" ref={rootRef}>
      <button type="button" className="ws-row-more" aria-haspopup="menu" aria-expanded={open} aria-label={`More for ${pipeline.name}`} onClick={() => setOpen((value) => !value)}><MoreHorizontal size={16} /></button>
      {open && (
        <div className="ws-row-menu" role="menu" aria-label={`More for ${pipeline.name}`}>
          {!current && <button type="button" role="menuitem" className="ws-menu-item" onClick={() => run(onCurrent)}><Check size={15} />Make current</button>}
          <button type="button" role="menuitem" className="ws-menu-item" onClick={() => run(onSharing)}><ArrowRightLeft size={15} />Profile sharing</button>
          <button type="button" role="menuitem" className="ws-menu-item" onClick={() => run(onImport)}><FileSpreadsheet size={15} />Import workflow</button>
          <div className="ws-menu-sep" />
          <button type="button" role="menuitem" className="ws-menu-item" disabled={pipeline.is_default} title={pipeline.is_default ? "The default pipeline cannot be deleted" : undefined} onClick={() => run(onDelete)} style={{ color: pipeline.is_default ? undefined : "#AD3B3B", opacity: pipeline.is_default ? 0.5 : 1 }}><Trash2 size={15} />Delete pipeline</button>
        </div>
      )}
    </div>
  );
}

function StageChain({ stages }) {
  if (stages === undefined) return <div className="crm-pipe-chain"><Skeleton height={26} radius={999} /></div>;
  if (!stages) return <p className="ws-muted crm-pipe-chain">Stages couldn’t be loaded. Open the workflow to see them.</p>;
  if (!stages.length) return <p className="ws-muted crm-pipe-chain">No stages yet. Open the workflow to add the first one.</p>;
  return (
    <ol className="crm-pipe-chain" aria-label="Stages">
      {stages.map((stage, index) => (
        <li key={stage.id} className={stage.is_closed ? (Number(stage.probability) >= 100 ? "is-won" : "is-lost") : ""}>
          <span>{stage.name}</span>
          {index < stages.length - 1 && <ChevronRight size={12} aria-hidden="true" />}
        </li>
      ))}
    </ol>
  );
}

function PipelineCard({ pipeline, workflow, current, deleting, confirming, sharing, onOpen, onLeads, onCurrent, onSharing, onImport, onConfirmDelete, onCancelDelete, onDelete }) {
  // undefined while loading, null when the workflow couldn't be read.
  const stages = workflow === undefined ? undefined : workflow?.stages || null;
  const steps = stages ? stages.reduce((count, stage) => count + (stage.nodes?.length || 0), 0) : null;
  const agentSteps = stages ? stages.reduce((count, stage) => count + (stage.nodes || []).filter((node) => node.node_type === "automation").length, 0) : null;
  return (
    <article className={`ws-card crm-pipe-card ws-rise${current ? " is-current" : ""}`}>
      <div className="crm-pipe-head">
        <span className="crm-pipe-icon" aria-hidden="true"><Workflow size={18} /></span>
        <div className="ws-grow">
          <h2 className="crm-pipe-name">
            {pipeline.name}
            {pipeline.is_default && <span className="ws-chip crm-chip-sm">Default</span>}
            {current && <span className="ws-chip crm-chip-sm crm-chip-current"><Check size={11} />Current</span>}
          </h2>
          <p className="ws-muted">{pipeline.description || "No description yet."}</p>
        </div>
        <PipelineMenu pipeline={pipeline} current={current} onCurrent={onCurrent} onSharing={onSharing} onImport={onImport} onDelete={onConfirmDelete} />
      </div>

      <StageChain stages={stages} />

      <dl className="crm-pipe-stats">
        <div><dt>Stages</dt><dd className="ws-num">{formatNumber(stages ? stages.length : pipeline.stages_count || 0)}</dd></div>
        <div><dt>Steps</dt><dd className="ws-num">{steps === null ? "—" : formatNumber(steps)}</dd></div>
        <div><dt>Leads</dt><dd className="ws-num">{pipeline.leads_count === undefined ? "—" : formatNumber(pipeline.leads_count)}</dd></div>
        <div className="crm-pipe-agents" title="Steps set to Automation run on their own">
          <dt>Agent steps</dt>
          <dd>{agent && agentSteps ? <img src={agent.image} alt="" /> : null}<span className="ws-num">{agentSteps === null ? "—" : formatNumber(agentSteps)}</span></dd>
        </div>
      </dl>

      {confirming ? (
        <div className="crm-pipe-confirm" role="alert">
          <CircleAlert size={16} />
          <span className="ws-grow">Delete {pipeline.name}? Pipelines with stages can’t be deleted.</span>
          <button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" disabled={deleting} onClick={onCancelDelete}>Cancel</button>
          <button type="button" className="ws-btn ws-btn--danger ws-btn--sm" disabled={deleting} onClick={onDelete}>{deleting && <Loader2 size={13} className="ws-spin" />}Delete</button>
        </div>
      ) : (
        <div className="crm-pipe-actions">
          <button type="button" className="ws-btn ws-btn--primary ws-btn--sm" onClick={onOpen}><GitBranch size={14} />Stages and steps</button>
          <Link className="ws-btn ws-btn--quiet ws-btn--sm" to={`/admin/lead-fields?pipeline=${pipeline.id}`}><Table2 size={14} />Lead table</Link>
          <button type="button" className="ws-btn ws-btn--plain" onClick={onLeads}>Open leads<ArrowRight size={13} /></button>
        </div>
      )}

      {sharing && <div className="crm-pipe-sharing"><PipelineSharingSettings key={pipeline.id} pipelineId={pipeline.id} /></div>}
    </article>
  );
}

export default function WorkflowEntry() {
  const navigate = useNavigate();
  const location = useLocation();
  const [pipelines, setPipelines] = useState([]);
  const [workflows, setWorkflows] = useState({});
  const [selectedId, setSelectedId] = useState(() => localStorage.getItem(SELECTED_PIPELINE_KEY) || "");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingPipeline, setEditingPipeline] = useState(null);
  const [sharingId, setSharingId] = useState(null);

  const loadPipelines = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await getPipelines();
      const list = Array.isArray(rows) ? rows : [];
      setPipelines(list);
      const savedId = localStorage.getItem(SELECTED_PIPELINE_KEY);
      const current = list.find(({ id }) => String(id) === String(savedId))
        || list.find(({ is_default }) => is_default)
        || list[0];
      const nextId = current?.id ? String(current.id) : "";
      setSelectedId(nextId);
      if (nextId) localStorage.setItem(SELECTED_PIPELINE_KEY, nextId);
      else localStorage.removeItem(SELECTED_PIPELINE_KEY);
      // Stage chains and agent steps; each pipeline loads on its own.
      const results = await Promise.allSettled(list.map((pipeline) => workflowService.getWorkflow(pipeline.id)));
      setWorkflows(Object.fromEntries(list.map((pipeline, index) => [pipeline.id, results[index].status === "fulfilled" ? results[index].value?.data || null : null])));
    } catch (requestError) {
      setError(getErrorMessage(requestError, "Unable to load pipelines."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPipelines(); }, [loadPipelines]);
  // ?create=1 (from Home, the palette or the New menu) opens the gallery once.
  useEffect(() => {
    if (new URLSearchParams(location.search).get("create") === "1") {
      setGalleryOpen(true);
      navigate(location.pathname, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const selectPipeline = (pipeline) => {
    const id = String(pipeline.id);
    localStorage.setItem(SELECTED_PIPELINE_KEY, id);
    clearAllStages();
    setSelectedId(id);
    toast.success(`${pipeline.name} is now the current pipeline`);
  };

  const handleCreated = async (pipeline, template) => {
    clearPipelineAndLeadsCache();
    clearAllStages();
    if (pipeline?.id) localStorage.setItem(SELECTED_PIPELINE_KEY, pipeline.id);
    setCreateOpen(false);
    setGalleryOpen(false);
    await loadPipelines();
    toast.success(template ? `${pipeline?.name || template.name} is ready with ${template.stages.length} stages. Add leads from Leads.` : "Pipeline created successfully");
  };

  const handleDelete = async (pipeline) => {
    setDeletingId(pipeline.id);
    try {
      await deletePipeline(pipeline.id);
      clearPipelineAndLeadsCache();
      clearAllStages();
      setConfirmingId(null);
      await loadPipelines();
      toast.success("Pipeline deleted successfully");
    } catch (deleteError) {
      toast.error(getErrorMessage(deleteError, "Unable to delete pipeline."));
    } finally {
      setDeletingId(null);
    }
  };

  const handleSheetUpdated = async () => {
    setEditingPipeline(null);
    clearPipelineAndLeadsCache();
    clearAllStages();
    await loadPipelines();
    toast.success("Pipeline updated from spreadsheet");
  };

  const totalAgentSteps = Object.values(workflows).reduce((count, workflow) => count + (workflow?.stages || []).reduce((sum, stage) => sum + (stage.nodes || []).filter((node) => node.node_type === "automation").length, 0), 0);

  return (
    <StagePage
      className="crm-pipes-page"
      eyebrow={<>Setup<span className="crm-eyebrow-dot" />Pipelines</>}
      title="Pipelines"
      subtitle={<>Each <HelpTip term="pipeline">pipeline</HelpTip> has its own <HelpTip term="stage">stages</HelpTip>, the <HelpTip term="step">steps</HelpTip> inside them and a <HelpTip term="leadtable">lead table</HelpTip>. Shape how leads move from enquiry to won.</>}
      actions={(
        <>
          <button type="button" className="crm-icon-btn" onClick={loadPipelines} disabled={loading} aria-label="Refresh pipelines" title="Refresh"><RefreshCw size={16} className={loading ? "ws-spin" : ""} /></button>
          <button type="button" onClick={() => setGalleryOpen(true)} className="ws-btn ws-btn--primary"><Plus size={16} />Create pipeline</button>
        </>
      )}
    >
      {!loading && !error && totalAgentSteps > 0 && (
        <div className="crm-pipes-agents ws-rise">
          {agent && <img src={agent.image} alt="" />}
          <span className="ws-grow"><b>{formatNumber(totalAgentSteps)} {totalAgentSteps === 1 ? "step runs" : "steps run"} on their own.</b> Steps set to Automation are done by an agent; your team handles the manual ones.</span>
        </div>
      )}

      {loading && !pipelines.length ? (
        <div className="crm-pipes-grid">{[0, 1].map((index) => <div key={index} className="ws-card ws-card-pad"><Skeleton width="45%" height={18} /><Skeleton height={26} radius={999} style={{ marginTop: 16 }} /><Skeleton height={60} radius={14} style={{ marginTop: 16 }} /></div>)}</div>
      ) : error ? (
        <div className="ws-notice" role="alert"><CircleAlert size={16} style={{ flex: "none", marginTop: 1 }} /><span className="ws-grow">{error}</span><button type="button" onClick={loadPipelines} className="ws-btn ws-btn--quiet ws-btn--sm"><RefreshCw size={13} />Try again</button></div>
      ) : pipelines.length === 0 ? (
        <section className="ws-card crm-table-state"><div className="crm-state"><span className="ws-icon-tile crm-state-icon" aria-hidden="true"><GitBranch size={18} /></span><h2 className="ws-h3">No pipelines yet</h2><p className="ws-muted">A pipeline is your sales process, step by step. Pick a ready-made one for your kind of business and change it any time.</p><div className="crm-state-actions"><button type="button" className="ws-btn ws-btn--primary" onClick={() => setGalleryOpen(true)}><Plus size={15} />Choose a template</button></div></div></section>
      ) : (
        <div className="crm-pipes-grid">
          {pipelines.map((pipeline) => (
            <PipelineCard
              key={pipeline.id}
              pipeline={pipeline}
              workflow={loading ? undefined : workflows[pipeline.id]}
              current={String(pipeline.id) === selectedId}
              deleting={deletingId === pipeline.id}
              confirming={confirmingId === pipeline.id}
              sharing={sharingId === pipeline.id}
              onOpen={() => navigate(`/admin/pipeline/${pipeline.id}`)}
              onLeads={() => { localStorage.setItem(SELECTED_PIPELINE_KEY, pipeline.id); clearPipelineAndLeadsCache(); navigate("/crm"); }}
              onCurrent={() => selectPipeline(pipeline)}
              onSharing={() => setSharingId(sharingId === pipeline.id ? null : pipeline.id)}
              onImport={() => setEditingPipeline(pipeline)}
              onConfirmDelete={() => setConfirmingId(pipeline.id)}
              onCancelDelete={() => setConfirmingId(null)}
              onDelete={() => handleDelete(pipeline)}
            />
          ))}
          <button type="button" className="crm-pipe-new" onClick={() => setGalleryOpen(true)}>
            <span className="crm-pipe-icon" aria-hidden="true"><Plus size={18} /></span>
            <b>New pipeline</b>
            <span className="ws-muted">For another product, team or kind of enquiry</span>
          </button>
        </div>
      )}

      {galleryOpen && (
        <PipelineTemplateGallery
          onClose={() => setGalleryOpen(false)}
          onCustom={() => { setGalleryOpen(false); setCreateOpen(true); }}
          onCreated={handleCreated}
        />
      )}
      <CreatePipelineDrawer isOpen={createOpen} onClose={() => setCreateOpen(false)} onCreated={handleCreated} />
      <EditPipelineSheetDrawer
        pipeline={editingPipeline}
        isOpen={Boolean(editingPipeline)}
        onClose={() => setEditingPipeline(null)}
        onUpdated={handleSheetUpdated}
      />
    </StagePage>
  );
}
